<template>
  <section
    v-if="isEditing || tabs.length"
    class="sticky top-[var(--sticky-top)] z-200 bg-background"
  >
    <ScrollSpy v-if="tabs.length" :tabs="tabs" :disabled="isEditing" />
  </section>
</template>

<script lang="ts" setup>
import { notNullish } from '@vueuse/core'
import { toValidHtmlId } from '~/helpers'
import { mapCheckboxTrue } from '#blokkli/runtime-helpers'
import type { FieldListItem } from '#blokkli/types'

const isEditing = false

const { rootBlocks } = defineBlokkliFragment({
  name: 'anchor_navigation',
  label: 'Anchor Navigation',
  description:
    'Displays an anchor navigation with links to title paragraphs on this page.',
})

type AnchorLink = {
  id: string
  label: string
}

function handleParagraph(collected: AnchorLink[], item: FieldListItem): void {
  if (
    item.bundle === 'title' &&
    mapCheckboxTrue(item.options?.showAnchor) === '1'
  ) {
    const label = item.props?.anchorTitle || item.props?.title
    if (label) {
      const id = toValidHtmlId(label)
      collected.push({ id, label })
    }
  } else if (item.bundle === 'from_library') {
    if (mapCheckboxTrue(item.options?.showAnchor) === '1') {
      const reusableParagraph = item.props?.libraryItem?.block as
        FieldListItem | undefined | null
      if (reusableParagraph && reusableParagraph.bundle === 'title') {
        const label =
          reusableParagraph.props?.anchorTitle || reusableParagraph.props?.title
        if (label) {
          const id = toValidHtmlId(label)
          collected.push({ id, label })
        }
      }
    }
  } else if (
    item.bundle === 'text_image_box' ||
    item.bundle === 'two_columns'
  ) {
    const nestedParagraphs = [
      item.props?.paragraphs,
      item.props?.paragraphsMedia,
      item.props?.paragraphsBox,
      item.props?.paragraphsLeft,
      item.props?.paragraphsRight,
    ]
      .filter(notNullish)
      .flat()
    if (nestedParagraphs.length) {
      collectAnchorLinks(collected, nestedParagraphs)
    }
  }
}

function collectAnchorLinks(
  collected: AnchorLink[],
  items?: FieldListItem[],
): AnchorLink[] {
  if (!items) {
    return collected
  }

  for (const item of items) {
    handleParagraph(collected, item)
  }

  return collected
}

const tabs = computed(() => collectAnchorLinks([], rootBlocks.value))
</script>
